import {
  InfiniteData,
  QueryFunction,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useMangoApi } from '~/atoms/MangoApi'
import { useUserState } from '~/atoms/UserState'
import { getPaginatedResultParts } from '~/utils/apiQueryUtils'

import {
  ExtensionInstance,
  ExtensionPreview,
  ExtensionType,
  PaginatedApiResult,
} from '@mangovoice/types'
import { ApiError } from '@mangovoice/types'

interface GetPbxLineExtensionsParams {
  pbxId: string | number
  options?: Omit<
    UseQueryOptions<PaginatedApiResult<ExtensionPreview>, ApiError>,
    'queryKey'
  >
}
export type ExtensionTypeValue = `${ExtensionType}`
const extensionTypePrefix = 'Extension'
export type ExtensionTypeLabel =
  `${ExtensionType extends `${typeof extensionTypePrefix}${infer R}` ? R : ExtensionType}`
export const extensionType = (type: ExtensionTypeLabel): ExtensionTypeValue =>
  `${extensionTypePrefix}${type}`

interface GetExtensionsParams {
  type?: ExtensionTypeLabel
  search?: string
  options?: Omit<
    UseInfiniteQueryOptions<PaginatedApiResult<ExtensionInstance>, ApiError>,
    'queryKey'
  >
}
const pageSize = 100

export const useGetPbxLineExtensions = ({
  pbxId,
  options,
}: GetPbxLineExtensionsParams) => {
  const api = useMangoApi()
  const query = useQuery<PaginatedApiResult<ExtensionPreview>, ApiError>({
    queryKey: ['pbx_line_extensions', pbxId],
    queryFn: async () => {
      const { ok, data } = await api.getPbxExtensions(
        pbxId,
        extensionType('Line'),
      )

      if (!ok) {
        return Promise.reject(data as ApiError)
      }
      const extensions = data as PaginatedApiResult<ExtensionPreview>
      return extensions
    },
    ...options,

    gcTime: options?.gcTime ?? 15 * 60 * 1000,
    retry: false,
  })

  return {
    ...query,
  }
}

export const useGetExtensions = ({
  type,
  search,
  options,
}: GetExtensionsParams) => {
  const { pbxId } = useUserState()
  const mangoApi = useMangoApi()
  const queryKey = useMemo(() => {
    const key = ['extensions', pbxId, `type-${type}`, search]
    return key
  }, [pbxId, type, search])

  const queryFn: QueryFunction<
    PaginatedApiResult<ExtensionInstance>,
    QueryKey,
    unknown
  > = async ({ pageParam = 0 }) => {
    const emptyResult = { count: 0, next: null, previous: null, results: [] }
    if (pbxId === 0) {
      return Promise.resolve(emptyResult)
    }
    const offset = (pageParam ?? 0) as number
    const { ok, data } = await mangoApi.getExtensions({
      pbx_id: pbxId,
      offset,
      limit: pageSize,
      type,
      search,
    })

    if (!ok || !data) {
      return Promise.reject(data as ApiError)
    }

    return data
  }

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    ...options,
    initialPageParam: 0,
    retry: (options?.retry as number | boolean | undefined) ?? false,
    getNextPageParam: (lastPage) => {
      if (lastPage.next === null) {
        return null
      }
      return getPaginatedResultParts(lastPage)?.nextPageOffset ?? null
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.previous === null) {
        return null
      }
      return getPaginatedResultParts(firstPage)?.previousPageOffset ?? null
    },
  })

  const data = query.data as unknown as InfiniteData<
    PaginatedApiResult<ExtensionInstance>,
    unknown
  >

  return {
    ...query,
    results: data?.pages.flatMap((page) => page.results) ?? [],
  }
}

interface UseGetPbxExtensionsParams {
  onQuerySuccess?: (data: ExtensionInstance) => void
  onQueryErrored?: (extensionId: number | string, error: ApiError) => void
  options?: Omit<UseQueryOptions<ExtensionInstance, ApiError>, 'queryKey'>
}
export const useGetExtensionInstance = ({
  onQuerySuccess,
  onQueryErrored,
  options,
}: UseGetPbxExtensionsParams) => {
  const api = useMangoApi()
  const [extensionId, setExtensionId] = useState<
    { val: number | string } | undefined
  >(undefined)

  const query = useQuery<ExtensionInstance, ApiError>({
    queryKey: ['extension_instance', extensionId],
    queryFn: async () => {
      if (!extensionId?.val)
        return Promise.reject({ message: 'Extension ID is required.' })

      const { ok, data, status } = await api.getExtensionInfo(extensionId.val)

      if (!ok) {
        onQueryErrored?.(extensionId.val, {
          status,
          ...data,
        })
        return Promise.reject(data)
      }

      onQuerySuccess?.(data!)
      return data!
    },
    ...options,
    retry: false,
  })

  const refetch = (newExtensionId: number) => {
    setExtensionId({
      val: newExtensionId,
    })
  }

  useEffect(() => {
    if (extensionId?.val) {
      query.refetch()
    }
  }, [extensionId])

  return {
    ...query,
    refetch,
  }
}
